import { CheckCircle2, AlertTriangle, X } from "lucide-react";

/**
 * UploadReconciliation — a compact ledger that closes the loop on every
 * batch. The user can see at a glance whether every file they dropped made
 * it through the pipeline: uploaded → accepted → done/skipped/failed/pending.
 * No more guessing whether "6 files are missing".
 *
 * Props:
 *   lastUpload    — { uploadedCount, acceptedFiles, skipped[], at }
 *   stats         — same shape used by StatsSection (total, qualifying, skipped, failed)
 *   pendingCount  — number still in pending|processing
 *   onDismiss     — clear the panel
 */
export default function UploadReconciliation({ lastUpload, stats, pendingCount, onDismiss }) {
  if (!lastUpload) return null;

  const uploaded = lastUpload.uploadedCount ?? 0;
  const accepted = lastUpload.acceptedFiles ?? uploaded;
  const skippedAtFinalize = Array.isArray(lastUpload.skipped) ? lastUpload.skipped : [];
  const lostInFlight = Math.max(0, uploaded - accepted);

  const done = stats.qualifying ?? 0;
  const skipped = stats.skipped ?? 0; // no qualifying injury — not a failure
  const failed = stats.failed ?? 0;
  const pending = pendingCount ?? 0;

  // Reconciliation: any file uploaded that hasn't yet been accounted for in
  // done/skipped/failed/pending is "unaccounted" and points to a real bug.
  const accounted = done + skipped + failed + pending;
  const total = stats.total ?? 0;
  const unaccounted = Math.max(0, total - accounted);

  const allClean = lostInFlight === 0 && skippedAtFinalize.length === 0 && unaccounted === 0;
  const stillProcessing = pending > 0;

  return (
    <section
      data-testid="upload-reconciliation"
      className={`border-2 ${allClean ? "border-black" : "border-red-700"} bg-white px-4 py-3`}
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] font-bold">
          {allClean ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-red-700" />
          )}
          Upload reconciliation
          {stillProcessing && (
            <span className="font-normal opacity-60 normal-case tracking-normal ml-2">
              · extraction in progress
            </span>
          )}
        </div>
        <button
          data-testid="reconciliation-dismiss-btn"
          onClick={onDismiss}
          className="opacity-60 hover:opacity-100"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Cell label="Uploaded" value={uploaded} testId="recon-uploaded" />
        <Cell
          label="Accepted"
          value={accepted}
          warn={lostInFlight > 0}
          sub={lostInFlight > 0 ? `${lostInFlight} lost on wire` : null}
          testId="recon-accepted"
        />
        <Cell label="Qualifying" value={done} tone="black" testId="recon-done" />
        <Cell label="Skipped" value={skipped} sub="No qualifying injury" testId="recon-skipped" />
        <Cell label="Failed" value={failed} tone={failed > 0 ? "red" : ""} testId="recon-failed" />
        <Cell
          label="Pending"
          value={pending}
          sub={pending > 0 ? "Still extracting" : null}
          testId="recon-pending"
        />
      </div>

      {(skippedAtFinalize.length > 0 || lostInFlight > 0 || unaccounted > 0) && (
        <div className="mt-3 border-t border-black/10 pt-3 space-y-2 font-mono text-[11px] tracking-[0.06em]">
          {lostInFlight > 0 && (
            <p data-testid="recon-lost-warn" className="text-red-700">
              <strong>{lostInFlight}</strong> file{lostInFlight === 1 ? "" : "s"} did not reach the server.
              Re-upload them.
            </p>
          )}
          {skippedAtFinalize.length > 0 && (
            <div data-testid="recon-skipped-list">
              <p className="font-bold uppercase tracking-[0.18em] text-[10px] mb-1">
                Could not assemble ({skippedAtFinalize.length}):
              </p>
              <ul className="list-disc list-inside opacity-80">
                {skippedAtFinalize.slice(0, 5).map((s, i) => (
                  <li key={i}>
                    <span className="font-bold">{s.file_name || "unknown"}</span>
                    {s.reason ? <> — {s.reason}</> : null}
                  </li>
                ))}
                {skippedAtFinalize.length > 5 && (
                  <li>…and {skippedAtFinalize.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
          {unaccounted > 0 && !stillProcessing && (
            <p data-testid="recon-unaccounted-warn" className="text-red-700">
              <strong>{unaccounted}</strong> file{unaccounted === 1 ? "" : "s"} accepted by the server
              but no status yet. Click <em>Resume stuck</em> above.
            </p>
          )}
        </div>
      )}
    </section>
  );
}

function Cell({ label, value, sub, warn, tone, testId }) {
  const toneClass =
    tone === "black"
      ? "bg-black text-white"
      : tone === "red"
      ? "bg-red-700 text-white"
      : warn
      ? "bg-amber-100"
      : "bg-zinc-50";
  return (
    <div data-testid={testId} className={`border border-black px-3 py-2 ${toneClass}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] opacity-70">{label}</div>
      <div className="font-mono text-2xl font-bold leading-none mt-1">{value}</div>
      {sub && (
        <div className="font-mono text-[9px] uppercase tracking-[0.18em] opacity-60 mt-1">
          {sub}
        </div>
      )}
    </div>
  );
}
